/**
 * 入场：跳动的粒子爱心 → 轻触 → 坍缩、画面崩溃 → 爆炸 → 照片星按日期先后落回螺线，装饰粒子散成天幕。
 * 节奏、时长和原型一致（docs/prototype-v0.1.md）：
 * 爱心淡入 1.4 秒；不点也会在 6.5 秒后自动继续（减少动态效果时 2.5 秒）；坍缩 1.15 秒；爆炸 3.4 秒（跳过时 1.8 秒）。
 */
import * as THREE from 'three';
import type { CameraPose, Galaxy } from './galaxy.ts';
import type { Sound } from './sound.ts';

export interface IntroView {
  /** 心跳时轻轻放大「轻触心跳」按钮 */
  pulse(beat: number): void;
  /** 已经点了或者自动继续：收起按钮，标题留到爆炸再淡出 */
  triggered(): void;
  /** 爆炸开始：文字淡出 */
  leave(): void;
  /** 全部结束：从页面上拿掉 */
  remove(): void;
  /** 点到它算「跳过」，点别处算「轻触」 */
  readonly skip: HTMLElement;
}

type Trigger = 'tap' | 'skip' | 'auto';

/** 一次“咚—哒”：第一下强，第二下弱 */
const beatAt = (ph: number) => Math.min(1, Math.exp(-(((ph - 0.05) / 0.045) ** 2)) + 0.55 * Math.exp(-(((ph - 0.27) / 0.055) ** 2)));
const smooth = (a: number, b: number, x: number) => {
  const t = Math.min(1, Math.max(0, (x - a) / (b - a)));
  return t * t * (3 - 2 * t);
};
const easeInOutSine = (t: number) => -(Math.cos(Math.PI * t) - 1) / 2;
const easeOutCubic = (t: number) => 1 - Math.pow(1 - t, 3);
const easeInOutCubic = (t: number) => (t < 0.5 ? 4 * t * t * t : 1 - Math.pow(-2 * t + 2, 3) / 2);

export function playIntro(g: Galaxy, view: IntroView, sound: Sound, opts: { reduced: boolean; onBang?: () => void }): Promise<void> {
  const { reduced } = opts;
  const cam = g.camera, post = g.postUniforms, layers = g.layers;
  const look = new THREE.Vector3(0, -5, 0), base = new THREE.Vector3();
  // 心宽约 30、高约 27，跳动时再大一些；横竖屏都要放得下
  const heartDistance = () => {
    const t = Math.tan(THREE.MathUtils.degToRad(cam.fov) / 2);
    return Math.max(36 / (2 * t * cam.aspect * 0.86), 27 / (2 * t * 0.46), 52);
  };

  let phase: 'heart' | 'collapse' | 'bang' = 'heart';
  let why: Trigger = 'auto';
  let t = 0, beatT = 0, lastBeat = -1, rate = 1, collapse = 0, bang = 0, shake = 0, glitch = 0, flash = 0, heartIn = 0;
  let flyFrom = new THREE.Vector3(), lookFrom = new THREE.Vector3(), home: CameraPose = g.overviewPose();
  const lookNow = new THREE.Vector3(), jitter = new THREE.Vector3(), center = new THREE.Vector3();
  const autoAt = reduced ? 2.5 : 6.5;

  g.beginIntro();
  for (const layer of layers) layer.uniforms.uOpacity.value = 0;
  base.set(0, 0, heartDistance());
  cam.position.copy(base);
  cam.lookAt(look);

  return new Promise<void>((resolve) => {
    const onDown = (e: PointerEvent) => trigger(e.target instanceof Node && view.skip.contains(e.target) ? 'skip' : 'tap');
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') trigger('skip');
      else if (e.key === 'Enter' || e.key === ' ') trigger('tap');
    };
    addEventListener('pointerdown', onDown, true);
    addEventListener('keydown', onKey, true);

    function trigger(w: Trigger): void {
      if (phase !== 'heart') return;
      why = w;
      removeEventListener('pointerdown', onDown, true);
      removeEventListener('keydown', onKey, true);
      view.triggered();
      // 只有真的点了，浏览器才允许出声
      if (w !== 'auto') sound.unlock();
      if (w === 'skip') startBang();
      else {
        phase = 'collapse';
        t = 0;
      }
    }

    function startBang(): void {
      phase = 'bang';
      t = 0;
      shake = 0;
      if (why === 'skip') collapse = 0;
      sound.boom();
      // 背景音乐从爆炸开始淡入（没点过、或者关了音乐时不出声）
      sound.start(4);
      view.leave();
      opts.onBang?.();
      cam.position.copy(base);
      cam.lookAt(look);
      cam.updateMatrixWorld();
      // 冲击波：两圈向外的流体推力，从爱心的中心把整个画面震开
      center.set(0, 0, 0).project(cam);
      if (!reduced) g.fluid.shockwave((center.x + 1) / 2, (center.y + 1) / 2);
      flyFrom = cam.position.clone();
      lookFrom = look.clone();
      home = g.overviewPose();
    }

    function finish(): void {
      g.onFrame = null;
      post.uFlash.value = 0;
      post.uGlitch.value = 0;
      g.endIntro(home);
      view.remove();
      resolve();
    }

    g.onFrame = (dt, time) => {
      t += dt;
      if (phase === 'heart') {
        heartIn = easeInOutSine(Math.min(1, t / 1.4));
        if (t >= autoAt) trigger('auto');
      } else if (phase === 'collapse') {
        const k = Math.min(1, t / (reduced ? 0.6 : 1.15));
        collapse = k * k;
        rate = 1 + k * 2.6;
        shake = reduced ? 0 : k * k * 0.9;
        glitch = reduced ? 0 : smooth(0.72, 1, k) * 0.9;
        if (k >= 1) startBang();
      }

      if (phase === 'bang') {
        const dur = why === 'skip' ? 1.8 : 3.4;
        bang = Math.min(1, t / dur);
        flash = (reduced ? 0.35 : 1) * (1 - easeOutCubic(Math.min(1, t / 0.9)));
        glitch = reduced ? 0 : 0.9 * Math.max(0, 1 - t / 0.55);
        // 镜头从爱心前拉远到能看到整片星海的位置；螺线、年份和中心光晕在后半段淡入
        const e = easeInOutCubic(Math.min(1, t / (dur + 0.4)));
        cam.position.lerpVectors(flyFrom, home.pos, e);
        lookNow.lerpVectors(lookFrom, home.target, e);
        cam.lookAt(lookNow);
        g.setScenery(smooth(dur * 0.62, dur * 0.62 + 2.0, t));
        if (t >= Math.max(dur + 0.4, dur * 0.62 + 2.0)) return finish();
      }

      // 心跳：约 73 次/分，坍缩时加快；点过之后才有声音
      const beating = phase !== 'bang';
      beatT += (dt * rate) / 0.82;
      const beat = beating ? beatAt(beatT % 1) : 0, whole = Math.floor(beatT);
      if (beating && whole !== lastBeat) {
        lastBeat = whole;
        sound.heartbeat(0.82 / rate, phase === 'collapse' ? 1.3 : 0.8);
      }
      for (const layer of layers) {
        const u = layer.uniforms;
        u.uBeat.value = beat;
        u.uSway.value = Math.sin(time * 0.55) * 0.38 * (1 - collapse);
        u.uCollapse.value = collapse;
        u.uBang.value = phase === 'bang' ? bang : 0;
        u.uOpacity.value = heartIn;
      }
      if (beating) {
        base.set(0, 0, heartDistance());
        cam.position.copy(base).add(jitter.set((Math.random() - 0.5) * shake, (Math.random() - 0.5) * shake, 0));
        cam.lookAt(look);
        view.pulse(beat);
      }
      post.uFlash.value = flash;
      post.uGlitch.value = glitch;
    };
  });
}
